import Chart from 'chart.js/auto'
import 'chartjs-adapter-date-fns'

function getExistingChart(canvas) {
    if (!window.Chart || !Chart.getChart || !canvas) return null
    return Chart.getChart(canvas) || null
}

export function fundChart(payload = {}) {
    return {
        payload,
        chart: null,
        _inited: false,
        _destroyed: false,

        init() {
            if (this._inited) return
            this._inited = true

            this.$nextTick(async () => {
                this._initChart()
            })
        },

        _buildSeries() {
            if (
                Array.isArray(this.payload.series) &&
                this.payload.series.length
            ) {
                return this.payload.series
                    .map((s) => {
                        const pts = (s.points || [])
                            .map((p) => {
                                const xRaw = p.x || p.date
                                const yRaw = p.y ?? p.val ?? p.indexValue
                                if (!xRaw) return null
                                const sX = String(xRaw)
                                const x = sX.length === 7 ? `${sX}-01` : sX
                                const y = Number(String(yRaw).replace(',', '.'))
                                if (!Number.isFinite(y)) return null
                                return { x, y }
                            })
                            .filter(Boolean)
                            .sort((a, b) =>
                                a.x < b.x ? -1 : a.x > b.x ? 1 : 0
                            )

                        if (!pts.length) return null

                        return {
                            label: s.label || '',
                            color: s.color || null,
                            points: pts,
                        }
                    })
                    .filter(Boolean)
            }

            const labels = this.payload.labels || []
            const values = this.payload.data || []

            const toYmd = (d) => {
                if (!d) return null
                const s = String(d)
                return s.length === 7 ? `${s}-01` : s
            }

            const pts = []
            for (let i = 0; i < Math.min(labels.length, values.length); i++) {
                const x = toYmd(labels[i])
                const y = Number(String(values[i]).replace(',', '.'))
                if (!x || !Number.isFinite(y)) continue
                pts.push({ x, y })
            }
            pts.sort((a, b) => (a.x < b.x ? -1 : a.x > b.x ? 1 : 0))

            return [
                {
                    label: this.payload.subtitle || 'Index',
                    points: pts,
                },
            ]
        },

        _initChart() {
            if (this._destroyed) return
            const canvas = this.$refs.canvas
            if (!canvas) return

            const existing = getExistingChart(canvas)
            if (existing) existing.destroy()
            if (this.chart) {
                try {
                    this.chart.destroy()
                } catch (_) {}
                this.chart = null
            }

            const ctx = canvas.getContext('2d')
            if (!ctx) {
                console.warn('[fundChart] Canvas saknar 2D-context')
                return
            }

            const series = this._buildSeries()
            if (!series.length) {
                console.warn('[fundChart] Inga serier att rita')
                return
            }

            const datasets = []
            const allPoints = []
            const colors = ['#4fa032', '#4a2583', '#afafaf', '#6075ad']

            series.forEach((s, idx) => {
                const color = s.color || colors[idx % colors.length]
                datasets.push({
                    label: s.label || `Serie ${idx + 1}`,
                    data: s.points,
                    tension: 0.25,
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    pointHitRadius: 0,
                    borderColor: color,
                    backgroundColor: color,
                    fill: false,
                    spanGaps: true,
                })
                allPoints.push(...s.points)
            })

            allPoints.sort((a, b) => (a.x < b.x ? -1 : a.x > b.x ? 1 : 0))
            const minX = allPoints[0].x
            const maxX = allPoints[allPoints.length - 1].x
            const yVals = allPoints.map((p) => p.y)
            const yMin = Math.min(...yVals)
            const yMax = Math.max(...yVals)
            const yMargin = (yMax - yMin) * 0.05

            const nf = (v) => {
                try {
                    return new Intl.NumberFormat(undefined, {
                        maximumFractionDigits: 2,
                    }).format(v)
                } catch {
                    return v
                }
            }

            const APTOS =
                '"aptos-display", "Aptos Display", Aptos, ui-sans-serif, system-ui, sans-serif'
            Chart.defaults.font.family = APTOS
            Chart.defaults.font.size = 14
            Chart.defaults.font.weight = 500
            Chart.defaults.font.style = 'normal'
            Chart.defaults.color = '#000000'

            this.chart = new Chart(ctx, {
                type: 'line',
                data: {
                    datasets,
                },
                options: {
                    parsing: {
                        xAxisKey: 'x',
                        yAxisKey: 'y',
                    },
                    animation: false,
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: true,
                            position: 'bottom',
                            align: 'start',
                        },
                        tooltip: {
                            callbacks: {
                                title: (items) => items[0]?.label || '',
                                label: (item) =>
                                    `${item.dataset.label}: ${nf(item.parsed.y)}`,
                            },
                        },
                    },
                    scales: {
                        x: {
                            type: 'time',
                            min: minX,
                            max: maxX,
                            time: {
                                parser: 'yyyy-MM-dd',
                                unit: 'year',
                                stepSize: 1,
                                displayFormats: { year: 'yyyy' },
                            },
                            afterBuildTicks: (scale) => {
                                const step = scale.width < 700 ? 3 : 1
                                const lastYear = new Date(maxX).getFullYear()
                                scale.ticks = scale.ticks.filter((t) => {
                                    const year = new Date(t.value).getFullYear()
                                    return (lastYear - year) % step === 0
                                })
                            },
                            ticks: {
                                autoSkip: false,
                                maxRotation: 0,
                                callback: (val) => new Date(val).getFullYear(),
                            },
                            grid: { display: false },
                        },
                        y: {
                            suggestedMin: yMin - yMargin,
                            suggestedMax: yMax + yMargin,
                            ticks: {
                                callback: (v) => nf(v),
                            },
                        },
                    },
                },
            })
        },

        update(nextPayload) {
            this.payload = nextPayload || this.payload
            if (!this.chart) {
                this._initChart()
                return
            }

            const series = this._buildSeries()
            const datasets = []
            const colors = ['#0f766e', '#C8BED9', '#D9D9D9', '#6075ad']

            series.forEach((s, idx) => {
                const color = colors[idx % colors.length]
                datasets.push({
                    label: s.label || `Serie ${idx + 1}`,
                    data: s.points,
                    tension: 0.25,
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    pointHitRadius: 0,
                    borderColor: color,
                    backgroundColor: color,
                    fill: false,
                    spanGaps: true,
                })
            })

            this.chart.data.datasets = datasets
            this.chart.update('none')
        },

        destroy() {
            this._destroyed = true
            if (this.chart) {
                try {
                    this.chart.destroy()
                } catch (_) {}
                this.chart = null
            }
        },
    }
}
